import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
  fetchRestaurantById,
  fetchRestaurantRootAdmin,
  extractRestaurantName,
  extractRestaurantId,
  fetchUserRestaurants,
  isRestaurantActive,
  isRestaurantDefault,
  normalizeIsDefaultFormValue,
  normalizeAddressesForTable,
  updateRestaurant,
  updateRestaurantAddress,
  normalizeRestaurantShortCode,
  fetchRestaurantMealDeliveryTimes,
  updateRestaurantMealDeliveryTimes,
  fetchRestaurantMyeatsDeliverySlots,
  updateRestaurantMyeatsDeliverySlots,
  fetchRestaurantServices,
  updateRestaurantService,
  updateRestaurantUser,
} from './restaurantApi';
import AddressTable from './AddressTable';
import RestaurantServicesDisplay from './RestaurantServicesDisplay';
import RestaurantTaxesDisplay from './RestaurantTaxesDisplay';
import OrderNumberingDisplay from './OrderNumberingDisplay';
import MealDeliveryTimesEditor, { buildRowsFromApiPayload } from './MealDeliveryTimesEditor';
import DeliverySlotsEditor, {
  emptyDeliverySlots,
  normalizeDeliverySlots,
  normalizeSlotDurationMinutes,
  DEFAULT_SLOT_DURATION_MINUTES,
} from './DeliverySlotsEditor';
import KitchenMediaEditor from './KitchenMediaEditor';
import WorkEatsMapOfficesPanel from './WorkEatsMapOfficesPanel';
import WorkEatsOrgMealDeliveryTimesEditor from './WorkEatsOrgMealDeliveryTimesEditor';
import CateringMapFarmhousesPanel from './CateringMapFarmhousesPanel';
import JrEatsMapSchoolsPanel from './JrEatsMapSchoolsPanel';
import PartyHallServiceFields from './PartyHallServiceFields';
import PartyHallImages from './PartyHallImages';
import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import {
  PARTY_HALL_SERVICE_TYPE,
  buildServicePayload,
  emptyServiceForm,
  extractPartyHallFields,
  formatHallType,
  validateServicePayload,
} from './restaurantServiceConstants';
import './css/Restaurants.css';
import '../admin/EatsAdminDashboard.css';
import '../admin/EatsAdminCreatePage.css';
import './css/OrderDetails.css';
import './css/restaurantServices.css';import {
  normalizeAdminEntityStatus,
  isInactiveEntityStatus,
  formatAadharDisplayOrFallback,
  formatFssaiDisplayOrFallback,
  formatMobileDisplayOrFallback,
} from './formatDisplay';
import { canEditRestaurantStoreProfile } from './deliveryAgentSession';
import { confirmAction } from './ui/feedback';
import RootAdminAssignPanel from '../admin/RootAdminAssignPanel';
import EatsAdminPageHeader from '../admin/EatsAdminPageHeader';
import OwnerDetailsSection from './OwnerDetailsSection';

const ADMIN_RESTAURANTS_PATH = '/eats/admin?tab=restaurants';

const SectionHeadingActions = ({
  canEdit,
  editing,
  onEdit,
  onCancel,
  onSave,
  saving = false,
}) => {
  if (!canEdit) return null;
  if (!editing) {
    return (
      <button type="button" className="eats-admin-header-btn" onClick={onEdit}>
        Edit
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        className="eats-admin-header-btn"
        onClick={onCancel}
        disabled={saving}
      >
        Cancel
      </button>
      <button
        type="button"
        className="eats-admin-header-btn"
        onClick={onSave}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </>
  );
};

const PHONE_QUERY = '(max-width: 767px)';

function useIsPhone() {
  const [isPhone, setIsPhone] = useState(() => window.matchMedia?.(PHONE_QUERY).matches ?? false);
  useEffect(() => {
    const mql = window.matchMedia?.(PHONE_QUERY);
    if (!mql) return undefined;
    const onChange = (e) => setIsPhone(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isPhone;
}

const RestaurantDetail = ({ restaurantId: restaurantIdProp, embeddedInAdmin = false }) => {
  const { restaurantId: routeRestaurantId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const restaurantId = restaurantIdProp ?? routeRestaurantId;
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const readOnlyView = searchParams.get('view') === 'readonly';
  const isPhone = useIsPhone();
  const canManageMaps = canEditRestaurantStoreProfile() && !readOnlyView && !isPhone;
  // Core restaurant edit/delete only in Eats admin; business portal is view + maps/schedules.
  const canEdit = Boolean(embeddedInAdmin) && canManageMaps;
  const canEditSchedule = canManageMaps;
  const { enabledTypes: enabledServiceTypes, loading: servicesLoading } =
    useRestaurantEnabledServices();

  const [restaurant, setRestaurant] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingRestaurant, setIsEditingRestaurant] = useState(false);
  const [isEditingTimings, setIsEditingTimings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingTimings, setSavingTimings] = useState(false);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantShortCode, setRestaurantShortCode] = useState('');
  const [contactNo, setContactNo] = useState('');
  const [email, setEmail] = useState('');
  const [cuisineType, setCuisineType] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [isDefault, setIsDefault] = useState('');
  const [avgPrepTime, setAvgPrepTime] = useState('');
  const [avgDeliveryTimePerKm, setAvgDeliveryTimePerKm] = useState('');
  const [mealDeliveryTimes, setMealDeliveryTimes] = useState([]);
  const [deliveryMode, setDeliveryMode] = useState('REGULAR');
  const [deliverySlots, setDeliverySlots] = useState(() => emptyDeliverySlots());
  const [slotDurationMinutes, setSlotDurationMinutes] = useState(DEFAULT_SLOT_DURATION_MINUTES);
  const [partyPrepBaseMinutes, setPartyPrepBaseMinutes] = useState(45);
  const [partyPrepMinutesPerPack, setPartyPrepMinutesPerPack] = useState(2);
  const [mappedWorkEatsOffices, setMappedWorkEatsOffices] = useState([]);
  const [userRestaurants, setUserRestaurants] = useState([]);
  const [newDefaultRestaurantId, setNewDefaultRestaurantId] = useState('');
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [showAddOwner, setShowAddOwner] = useState(false);
  const [ownerReloadToken, setOwnerReloadToken] = useState(0);
  const [partyHallDetails, setPartyHallDetails] = useState(null);
  const [partyHallServiceId, setPartyHallServiceId] = useState(null);
  const [isEditingPartyHall, setIsEditingPartyHall] = useState(false);
  const [partyHallEditForm, setPartyHallEditForm] = useState(() => emptyServiceForm());
  const [partyHallSaving, setPartyHallSaving] = useState(false);
  const [servicesReloadToken, setServicesReloadToken] = useState(0);
  const initialTab = searchParams.get('tab') || 'details';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (!restaurant || searchParams.get('edit') !== '1') return;
    if (canEdit) setIsEditingRestaurant(true);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant]);

  useEffect(() => {
    if (!isPhone) return;
    setIsEditingRestaurant(false);
    setIsEditingTimings(false);
    setIsEditingSchedule(false);
    setIsEditingPartyHall(false);
    setShowAddOwner(false);
  }, [isPhone]);

  const activeOtherUserRestaurants = userRestaurants.filter(
    (row) =>
      String(extractRestaurantId(row)) !== String(restaurantId) &&
      isRestaurantActive(row)
  );
  const anotherActiveRestaurantIsDefault = activeOtherUserRestaurants.some(isRestaurantDefault);
  const isDefaultNo =
    isDefault === 'false' || isDefault === false || isDefault === '0' || isDefault === 0;
  const showNewDefaultPicker =
    isDefaultNo &&
    activeOtherUserRestaurants.length > 0 &&
    !anotherActiveRestaurantIsDefault;

  const handleIsDefaultChange = (value) => {
    if (value === '0' || value === 'false') {
      if (userRestaurants.length <= 1) {
        window.alert(
          'At least one default restaurant is required. This is your only restaurant.'
        );
        setIsDefault('1');
        setNewDefaultRestaurantId('');
        return;
      }
      if (activeOtherUserRestaurants.length === 0) {
        window.alert(
          'No other active restaurant is available to set as default.'
        );
        setIsDefault('1');
        setNewDefaultRestaurantId('');
        return;
      }
      const otherDefault = activeOtherUserRestaurants.find(isRestaurantDefault);
      if (otherDefault) {
        setNewDefaultRestaurantId('');
      } else if (activeOtherUserRestaurants.length === 1) {
        setNewDefaultRestaurantId(String(extractRestaurantId(activeOtherUserRestaurants[0])));
      } else {
        setNewDefaultRestaurantId('');
      }
    } else {
      setNewDefaultRestaurantId('');
    }
    setIsDefault(value);
  };

  const applyRestaurantToForm = useCallback((data) => {
    if (!data) return;
    setRestaurantName(data.restaurantName ?? extractRestaurantName(data));
    setRestaurantShortCode(String(data.restaurantShortCode ?? '').toUpperCase());
    const contact = data.contactNo;
    setContactNo(contact !== '' && contact != null ? String(contact) : '');
    setEmail(data.email ?? '');
    setCuisineType(data.cuisineType ?? '');
    setNotes(data.notes ?? '');
    setStatus(normalizeAdminEntityStatus(data.status ?? ''));
    setIsDefault(
      normalizeIsDefaultFormValue(
        data.isDefault ?? data.is_default ?? data.IsDefault
      )
    );
    setAvgPrepTime(
      data.avgPrepTime != null && data.avgPrepTime !== '' ? String(data.avgPrepTime) : ''
    );
    setAvgDeliveryTimePerKm(
      data.avgDeliveryTimePerKm != null && data.avgDeliveryTimePerKm !== ''
        ? String(data.avgDeliveryTimePerKm)
        : ''
    );
    setAddresses(data.addresses ?? normalizeAddressesForTable(data));
  }, []);

  const applyPartyHallService = useCallback((partyHallService) => {
    if (!partyHallService) {
      setPartyHallDetails(null);
      setPartyHallServiceId(null);
      setIsEditingPartyHall(false);
      setPartyHallEditForm(emptyServiceForm());
      return;
    }
    const hall = extractPartyHallFields(partyHallService);
    const details = {
      ...hall,
      hallTypeLabel: formatHallType(partyHallService),
      minGuestCount: partyHallService.minGuestCount ?? partyHallService.guestCount,
      maxGuestCount: partyHallService.maxGuestCount,
      status: partyHallService.status,
    };
    setPartyHallDetails(details);
    setPartyHallServiceId(partyHallService.restaurantServiceId ?? null);
  }, []);

  const loadRestaurant = useCallback(async () => {
    const id = restaurantId != null ? String(restaurantId).trim() : '';
    if (!id || id === 'null' || id === 'undefined') {
      setError('Invalid restaurant id.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await fetchRestaurantById(id);
      setRestaurant(data);
      applyRestaurantToForm(data);
      try {
        const timesPayload = await fetchRestaurantMealDeliveryTimes(id);
        setMealDeliveryTimes(buildRowsFromApiPayload(timesPayload));
      } catch (timesError) {
        console.warn('Meal delivery times fetch failed:', timesError);
        setMealDeliveryTimes(buildRowsFromApiPayload({}));
      }
      try {
        const slotsPayload = await fetchRestaurantMyeatsDeliverySlots(id);
        setDeliveryMode(slotsPayload?.deliveryMode || 'REGULAR');
        setDeliverySlots(normalizeDeliverySlots(slotsPayload?.deliverySlots));
        setSlotDurationMinutes(
          normalizeSlotDurationMinutes(
            slotsPayload?.slotDurationMinutes,
            DEFAULT_SLOT_DURATION_MINUTES,
          ),
        );
        setPartyPrepBaseMinutes(
          Number(slotsPayload?.partyPrepBaseMinutes ?? 45) || 45,
        );
        setPartyPrepMinutesPerPack(
          Number(slotsPayload?.partyPrepMinutesPerPack ?? 2) || 2,
        );
      } catch (slotsError) {
        console.warn('MYEATS delivery slots fetch failed:', slotsError);
        setDeliveryMode('REGULAR');
        setDeliverySlots(emptyDeliverySlots());
        setSlotDurationMinutes(DEFAULT_SLOT_DURATION_MINUTES);
        setPartyPrepBaseMinutes(45);
        setPartyPrepMinutesPerPack(2);
      }
      try {
        const serviceRows = await fetchRestaurantServices(id);
        const partyHallService = Array.isArray(serviceRows)
          ? serviceRows.find(
              (row) => String(row.serviceType || '').toUpperCase() === PARTY_HALL_SERVICE_TYPE
            )
          : null;
        applyPartyHallService(partyHallService);
      } catch (serviceError) {
        console.warn('Restaurant services fetch failed:', serviceError);
        applyPartyHallService(null);
      }
    } catch (e) {
      console.error('Restaurant detail fetch failed:', e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to load restaurant details.'
      );
      applyPartyHallService(null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, applyRestaurantToForm, applyPartyHallService]);

  const refreshPartyHallFromServices = useCallback(async () => {
    const id = restaurantId != null ? String(restaurantId).trim() : '';
    if (!id) return;
    try {
      const serviceRows = await fetchRestaurantServices(id);
      const partyHallService = Array.isArray(serviceRows)
        ? serviceRows.find(
            (row) => String(row.serviceType || '').toUpperCase() === PARTY_HALL_SERVICE_TYPE
          )
        : null;
      applyPartyHallService(partyHallService);
    } catch (serviceError) {
      console.warn('Restaurant services refresh failed:', serviceError);
    }
  }, [restaurantId, applyPartyHallService]);

  useEffect(() => {
    loadRestaurant();
  }, [loadRestaurant]);

  useEffect(() => {
    setShowAddOwner(false);
  }, [restaurantId]);

  useEffect(() => {
    if (!embeddedInAdmin || !restaurantId) {
      setOwnerDetails(null);
      setOwnerLoading(false);
      return;
    }

    const id = String(restaurantId).trim();
    if (!id || id === 'null' || id === 'undefined') {
      setOwnerDetails(null);
      return;
    }

    let cancelled = false;
    setOwnerLoading(true);
    fetchRestaurantRootAdmin(id)
      .then((owner) => {
        if (!cancelled) setOwnerDetails(owner);
      })
      .catch((error) => {
        console.warn('Restaurant owner fetch failed:', error);
        if (!cancelled) setOwnerDetails(null);
      })
      .finally(() => {
        if (!cancelled) setOwnerLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [embeddedInAdmin, restaurantId, ownerReloadToken]);

  useEffect(() => {
    const userId = user?.userId;
    if (!userId) {
      setUserRestaurants([]);
      return;
    }
    fetchUserRestaurants(userId)
      .then(setUserRestaurants)
      .catch(() => setUserRestaurants([]));
  }, [user?.userId]);

  useEffect(() => {
    if (embeddedInAdmin) return;
    const isEatsAppUser = String(user?.domain || '').toUpperCase() === 'EATS_APP';
    const id = restaurantId != null ? String(restaurantId).trim() : '';
    if (isEatsAppUser && id && id !== 'null' && id !== 'undefined') {
      navigate(`/eats/admin/restaurants/${id}`, { replace: true });
    }
  }, [embeddedInAdmin, navigate, restaurantId, user?.domain]);

  const authHeaders = () => {
    const token = localStorage.getItem('Token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleUpdateRestaurant = async () => {
    if (!restaurantId) return;

    if (showNewDefaultPicker && !newDefaultRestaurantId) {
      window.alert('Select another restaurant as default before saving.');
      return;
    }

    const shortCode = normalizeRestaurantShortCode(restaurantShortCode);
    if (!shortCode) {
      window.alert('Restaurant short code is required.');
      return;
    }

    const contactDigits = String(contactNo || '').replace(/\D/g, '');
    if (contactDigits.length !== 10) {
      window.alert('Please enter a valid 10-digit contact number.');
      return;
    }

    setSaving(true);
    let restaurantSaved = false;

    try {
      const result = await updateRestaurant(
        restaurantId,
        {
          restaurantName,
          restaurantShortCode: shortCode,
          contactNo,
          email,
          cuisineType,
          notes,
          isDefault,
          newDefaultRestaurantId: showNewDefaultPicker ? newDefaultRestaurantId : undefined,
          status,
          avgPrepTime,
          avgDeliveryTimePerKm,
          imageUrl: restaurant?.imageUrl ?? restaurant?.image,
          primaryAddress: addresses.length > 0 ? addresses[0] : null,
        },
        user?.userId ?? 1
      );

      restaurantSaved = true;

      setIsEditingRestaurant(false);
      setNewDefaultRestaurantId('');
      const name = restaurantName.trim();
      if (name) localStorage.setItem('restaurantName', name);
      await loadRestaurant();
      window.alert(result?.message || 'Restaurant updated successfully');
    } catch (e) {
      console.error('Update restaurant error:', e);
      const errData = e?.response?.data;
      if (Array.isArray(errData?.otherRestaurants) && errData.otherRestaurants.length > 0) {
        setUserRestaurants((prev) => {
          const merged = [...prev];
          for (const row of errData.otherRestaurants) {
            const id = row.restaurantId ?? row.restaurant_id;
            if (!id || merged.some((r) => String(extractRestaurantId(r)) === String(id))) {
              continue;
            }
            merged.push({
              restaurantId: id,
              restaurantName: row.restaurantName ?? row.restaurant_name,
            });
          }
          return merged;
        });
        setIsDefault('0');
        if (errData.otherRestaurants.length === 1) {
          const only = errData.otherRestaurants[0];
          setNewDefaultRestaurantId(
            String(only.restaurantId ?? only.restaurant_id ?? '')
          );
        }
      }
      window.alert(
        errData?.message ||
          errData?.error ||
          (restaurantSaved ? 'Restaurant saved but follow-up updates failed.' : 'Failed to update restaurant')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRestaurant = async () => {
    if (!restaurantId) return;

    const ok = await confirmAction({
      title: 'Delete restaurant?',
      message: 'Are you sure you want to delete this restaurant? This action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;

    try {
      await axiosRestaurantInstance.delete(RESTAURANT_API_PATHS.restaurantDeactivate(restaurantId), {
        headers: authHeaders(),
      });
      alert('Restaurant deactivated successfully');

      if (String(localStorage.getItem('restaurantId')) === String(restaurantId)) {
        localStorage.removeItem('restaurantId');
        localStorage.removeItem('restaurantName');
      }
      navigate(embeddedInAdmin ? ADMIN_RESTAURANTS_PATH : '/business/admin');
    } catch (e) {
      console.error('Delete restaurant error:', e);
      alert(e?.response?.data?.message || 'Failed to deactivate restaurant');
    }
  };

  const handleAddressUpdate = async (index, updatedAddress) => {
    if (!updatedAddress.addressId) {
      alert('Cannot update: missing address id');
      return;
    }

    try {
      await updateRestaurantAddress(
        updatedAddress.addressId,
        updatedAddress,
        user?.userId ?? 1
      );

      const updated = [...addresses];
      updated[index] = updatedAddress;
      setAddresses(updated);
      alert('Address updated successfully');
      await loadRestaurant();
    } catch (e) {
      console.error('Update address error:', e);
      alert(e?.response?.data?.message || 'Failed to update address');
    }
  };

  const handleCancelEdit = () => {
    setIsEditingRestaurant(false);
    if (restaurant) applyRestaurantToForm(restaurant);
  };

  const handleCancelTimingsEdit = () => {
    setIsEditingTimings(false);
    loadRestaurant();
  };

  const handleCancelScheduleEdit = () => {
    setIsEditingSchedule(false);
    loadRestaurant();
  };

  const handleSaveOwner = async (payload) => {
    if (!ownerDetails?.id) {
      throw new Error('Owner is not assigned.');
    }
    await updateRestaurantUser(ownerDetails.id, {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      mobile: payload.mobile,
      aadharNo: payload.aadharNo,
      restaurantId,
      restaurantName: restaurantName || extractRestaurantName(restaurant),
      updatedById: user?.userId ?? 1,
    });
    setOwnerReloadToken((token) => token + 1);
    window.alert('Owner updated successfully');
  };

  const handleSaveTimings = async () => {
    if (!restaurantId) return;
    setSavingTimings(true);
    try {
      await updateRestaurantMealDeliveryTimes(
        restaurantId,
        mealDeliveryTimes,
        user?.userId ?? 1,
      );
      setIsEditingTimings(false);
      await loadRestaurant();
      window.alert('Timings updated successfully');
    } catch (timesError) {
      console.error('Meal delivery times update failed:', timesError);
      window.alert(
        timesError?.response?.data?.message ||
          timesError?.response?.data?.error ||
          'Meal delivery times could not be saved.'
      );
    } finally {
      setSavingTimings(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!restaurantId) return;
    setSavingSchedule(true);
    try {
      await updateRestaurantMyeatsDeliverySlots(
        restaurantId,
        {
          deliveryMode,
          deliverySlots,
          slotDurationMinutes,
          partyPrepBaseMinutes: Number(partyPrepBaseMinutes) || 0,
          partyPrepMinutesPerPack: Number(partyPrepMinutesPerPack) || 0,
        },
        user?.userId ?? 1,
      );
      setIsEditingSchedule(false);
      await loadRestaurant();
      window.alert('Delivery options updated successfully');
    } catch (slotsError) {
      console.error('Delivery schedule update failed:', slotsError);
      window.alert(
        slotsError?.response?.data?.message ||
          slotsError?.response?.data?.error ||
          'Delivery options could not be saved.'
      );
    } finally {
      setSavingSchedule(false);
    }
  };

  const displayName = extractRestaurantName(restaurant) || restaurantName || 'Restaurant details';

  const detailTabs = useMemo(() => {
    const tabs = [];
    if (embeddedInAdmin) {
      tabs.push({ id: 'owner', label: 'Owner' });
    }
    tabs.push(
      { id: 'details', label: 'Profile' },
      { id: 'address', label: 'Address' },
      { id: 'timings', label: 'Timings' },
      { id: 'orderNumbering', label: 'Order Numbering' },
      { id: 'taxes', label: 'Taxes' },
      { id: 'services', label: 'Services' },
      { id: 'media', label: 'Media' },
      { id: 'deliveryOptions', label: 'Delivery Options' },
      { id: 'mapOffices', label: 'Map Offices' },
      { id: 'mapSchools', label: 'Map Schools' },
    );
    const hasCatering = Array.isArray(enabledServiceTypes)
      ? enabledServiceTypes.map((t) => String(t).toUpperCase()).includes('CATERING')
      : true;
    if (hasCatering) {
      tabs.push({ id: 'mapFarmhouses', label: 'Map Farmhouses' });
    }
    if (partyHallDetails) {
      tabs.push({ id: 'partyHall', label: 'Party Hall' });
    }
    return tabs;
  }, [embeddedInAdmin, enabledServiceTypes, partyHallDetails]);

  useEffect(() => {
    if (!detailTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab('details');
    }
  }, [activeTab, detailTabs]);

  useEffect(() => {
    const fromUrl = searchParams.get('tab');
    if (fromUrl && detailTabs.some((tab) => tab.id === fromUrl) && fromUrl !== activeTab) {
      setActiveTab(fromUrl);
    }
  }, [activeTab, detailTabs, searchParams]);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    if (isEditingRestaurant) {
      setIsEditingRestaurant(false);
      if (restaurant) applyRestaurantToForm(restaurant);
    }
    if (isEditingTimings) {
      setIsEditingTimings(false);
      loadRestaurant();
    }
    if (isEditingSchedule) {
      setIsEditingSchedule(false);
      loadRestaurant();
    }
    if (isEditingPartyHall) {
      setIsEditingPartyHall(false);
      setPartyHallEditForm(emptyServiceForm());
    }
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    if (tabId && tabId !== 'details') {
      next.set('tab', tabId);
    } else {
      next.delete('tab');
    }
    setSearchParams(next, { replace: true });
  };

  const displayIsDefault =
    isDefault === 'true' ||
    isDefault === true ||
    isDefault === '1' ||
    isDefault === 1
      ? 'Yes'
      : isDefault === 'false' ||
          isDefault === false ||
          isDefault === '0' ||
          isDefault === 0
        ? 'No'
        : '—';

  const displayValue = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
  };

  const DetailField = ({ label, value, wide = false, badge = false }) => (
    <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
      <span className="restaurant-detail-label">{label}</span>
      {badge ? (
        <span
          className={`restaurant-detail-badge restaurant-detail-badge-${String(value || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-') || 'unknown'}`}
        >
          {displayValue(value)}
        </span>
      ) : (
        <span className="restaurant-detail-value">{displayValue(value)}</span>
      )}
    </div>
  );

  const handleBack = () => {
    if (embeddedInAdmin) {
      navigate(ADMIN_RESTAURANTS_PATH);
      return;
    }
    navigate('/business/admin');
  };

  const startEditPartyHall = () => {
    if (!partyHallDetails) return;
    setIsEditingPartyHall(true);
    setPartyHallEditForm({
      ...emptyServiceForm(),
      serviceType: PARTY_HALL_SERVICE_TYPE,
      status: String(partyHallDetails.status || 'ACTIVE').trim().toUpperCase(),
      minGuestCount:
        partyHallDetails.minGuestCount != null ? String(partyHallDetails.minGuestCount) : '',
      maxGuestCount:
        partyHallDetails.maxGuestCount != null ? String(partyHallDetails.maxGuestCount) : '',
      hallName: partyHallDetails.hallName || '',
      hallType: partyHallDetails.hallType || '',
      description: partyHallDetails.description || '',
      basePrice: partyHallDetails.basePrice || '',
      amenities: partyHallDetails.amenities || '',
      eventTypes: partyHallDetails.eventTypes || '',
    });
  };

  const cancelEditPartyHall = () => {
    setIsEditingPartyHall(false);
    setPartyHallEditForm(emptyServiceForm());
  };

  const handleSavePartyHall = async () => {
    if (!partyHallServiceId) {
      window.alert('Party hall service was not found.');
      return;
    }

    const validationError = validateServicePayload(PARTY_HALL_SERVICE_TYPE, partyHallEditForm);
    if (validationError) {
      window.alert(validationError);
      return;
    }

    const built = buildServicePayload(PARTY_HALL_SERVICE_TYPE, partyHallEditForm);
    const payload = {
      minGuestCount: built.minGuestCount,
      maxGuestCount: built.maxGuestCount ?? null,
      partyHall: built.partyHall,
    };

    setPartyHallSaving(true);
    try {
      const result = await updateRestaurantService(partyHallServiceId, payload);
      const updatedService = result?.service;
      if (updatedService) {
        applyPartyHallService(updatedService);
      } else {
        const serviceRows = await fetchRestaurantServices(restaurantId);
        const partyHallService = Array.isArray(serviceRows)
          ? serviceRows.find(
              (row) => String(row.serviceType || '').toUpperCase() === PARTY_HALL_SERVICE_TYPE
            )
          : null;
        applyPartyHallService(partyHallService);
      }
      setIsEditingPartyHall(false);
      setPartyHallEditForm(emptyServiceForm());
      setServicesReloadToken((token) => token + 1);
    } catch (e) {
      console.error('Failed to update party hall details:', e);
      window.alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to update party hall details.'
      );
    } finally {
      setPartyHallSaving(false);
    }
  };

  const page = (
    <div
      className={`order-details restaurant-detail-page restaurant-detail-page-admin${
        !embeddedInAdmin ? ' restaurant-detail-page--shell' : ''
      }`}
    >
      <div className="restaurant-detail-panel">
        {embeddedInAdmin ? (
          <EatsAdminPageHeader
            title={displayName}
            onBack={handleBack}
            actions={
              restaurant && !loading && canEdit && activeTab === 'details' && !isInactiveEntityStatus(status) ? (
                <button
                  type="button"
                  className="eats-admin-header-btn restaurant-detail-delete-btn"
                  onClick={handleDeleteRestaurant}
                >
                  Delete
                </button>
              ) : null
            }
          />
        ) : null}

        {loading && <p className="restaurant-detail-message">Loading…</p>}
        {!loading && error && <p className="restaurant-detail-message error">{error}</p>}

        {!loading && !error && restaurant && (
          <div className="restaurant-detail-section">
            <div className="restaurant-detail-tabs">
              <select
                className="restaurant-detail-tabs__select"
                value={activeTab}
                onChange={(e) => handleTabChange(e.target.value)}
                aria-label="Restaurant section"
              >
                {detailTabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.label}
                  </option>
                ))}
              </select>
              <div className="restaurant-detail-tabs__list" role="tablist" aria-label="Restaurant sections">
                {detailTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`restaurant-detail-tab${
                      activeTab === tab.id ? ' restaurant-detail-tab--active' : ''
                    }`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {restaurantId ? (
                <a
                  className="restaurant-detail-tabs__profile-link"
                  href={`/restaurant/${restaurantId}/profile`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Profile view
                </a>
              ) : null}
            </div>

            {activeTab === 'details' ? (
            <div className="restaurant-detail-card" role="tabpanel">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Profile</h3>
                <div className="restaurant-detail-card-header-actions">
                  <SectionHeadingActions
                    canEdit={canEdit}
                    editing={isEditingRestaurant}
                    onEdit={() => setIsEditingRestaurant(true)}
                    onCancel={handleCancelEdit}
                    onSave={handleUpdateRestaurant}
                    saving={saving}
                  />
                </div>
              </div>

              {isEditingRestaurant ? (
                <div className="restaurant-detail-edit-form">
                  <div className="get-restaurant-form-row flex">
                <div className="get-restaurant-form-group">
                  <label>Restaurant Name</label>
                  <input
                    type="text"
                    value={restaurantName}
                    onChange={(e) => setRestaurantName(e.target.value)}
                    readOnly={!embeddedInAdmin}
                    disabled={!embeddedInAdmin}
                    className={embeddedInAdmin ? undefined : 'restaurant-detail-field-locked'}
                  />
                </div>
                <div className="get-restaurant-form-group">
                  <label>Restaurant Short Code</label>
                  <input
                    type="text"
                    value={restaurantShortCode}
                    onChange={(e) => setRestaurantShortCode(e.target.value.toUpperCase())}
                    readOnly={!embeddedInAdmin}
                    disabled={!embeddedInAdmin}
                    required={embeddedInAdmin}
                    className={embeddedInAdmin ? undefined : 'restaurant-detail-field-locked'}
                  />
                </div>
                <div className="get-restaurant-form-group">
                  <label>Contact Number</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    value={contactNo}
                    onChange={(e) =>
                      setContactNo(e.target.value.replace(/\D/g, '').slice(0, 10))
                    }
                  />
                </div>
              </div>

              <div className="get-restaurant-form-row flex">
                <div className="get-restaurant-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="get-restaurant-form-group">
                  <label>Cuisine Type</label>
                  <input
                    type="text"
                    value={cuisineType}
                    onChange={(e) => setCuisineType(e.target.value)}
                  />
                </div>
                <div className="get-restaurant-form-group">
                  <label>Is Default</label>
                  <select value={isDefault} onChange={(e) => handleIsDefaultChange(e.target.value)}>
                    <option value="">Select</option>
                    <option value="1">Yes</option>
                    <option value="0" disabled={userRestaurants.length <= 1}>
                      No
                    </option>
                  </select>
                  {userRestaurants.length <= 1 && (
                    <p className="get-restaurant-form-hint">
                      Your only restaurant must stay the default.
                    </p>
                  )}
                </div>
              </div>

              {showNewDefaultPicker && (
                <div className="get-restaurant-form-row flex">
                  <div className="get-restaurant-form-group">
                    <label>New default restaurant</label>
                    <select
                      value={newDefaultRestaurantId}
                      onChange={(e) => setNewDefaultRestaurantId(e.target.value)}
                    >
                      <option value="">Select restaurant</option>
                      {activeOtherUserRestaurants.map((row) => {
                        const id = extractRestaurantId(row);
                        return (
                          <option key={id} value={id}>
                            {extractRestaurantName(row) || `Restaurant ${id}`}
                          </option>
                        );
                      })}
                    </select>
                    <p className="get-restaurant-form-hint">
                      Choose which restaurant should be default before unsetting this one.
                    </p>
                  </div>
                </div>
              )}

              <div className="get-restaurant-form-row flex">
                <div className="get-restaurant-form-group">
                  <label>Avg Prep Time (min)</label>
                  <input
                    type="number"
                    value={avgPrepTime}
                    onChange={(e) => setAvgPrepTime(e.target.value)}
                    min="0"
                  />
                </div>
                <div className="get-restaurant-form-group">
                  <label>Avg Delivery Time / Km (min)</label>
                  <input
                    type="number"
                    value={avgDeliveryTimePerKm}
                    onChange={(e) => setAvgDeliveryTimePerKm(e.target.value)}
                    min="0"
                  />
                </div>
                <div className="get-restaurant-form-group">
                  <label>Status</label>
                  {embeddedInAdmin ? (
                    <select value={normalizeAdminEntityStatus(status)} onChange={(e) => setStatus(e.target.value)}>
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  ) : (
                    <input type="text" value={status} readOnly />
                  )}
                </div>
              </div>

              <div className="get-restaurant-form-row flex">
                <div className="get-restaurant-form-group get-restaurant-form-group-wide">
                  <label>Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
                </div>
              ) : (
                <div className="restaurant-detail-grid">
                  <div className="restaurant-detail-field">
                    <span className="restaurant-detail-label">Restaurant Name</span>
                    <span className="restaurant-detail-value restaurant-detail-name-value">
                      {displayValue(restaurantName)}
                      {status ? (
                        <span
                          className={`eats-admin-status-icon eats-admin-status-icon--inline eats-admin-status-icon--${
                            String(status).toUpperCase() === 'ACTIVE'
                              ? 'active'
                              : isInactiveEntityStatus(status)
                                ? 'inactive'
                                : 'default'
                          }`}
                          title={status}
                          aria-label={status}
                          role="img"
                        />
                      ) : null}
                    </span>
                  </div>
                  <DetailField label="Restaurant Short Code" value={restaurantShortCode} />
                  <DetailField label="Contact Number" value={contactNo} />
                  <DetailField label="Email" value={email} />
                  <DetailField label="Cuisine Type" value={cuisineType} />
                  <DetailField
                    label="FSSAI Number"
                    value={formatFssaiDisplayOrFallback(restaurant?.fssaiNumber)}
                  />
                  <DetailField label="Is Default" value={displayIsDefault} />
                  <DetailField label="Avg Prep Time (min)" value={avgPrepTime} />
                  <DetailField label="Avg Delivery Time / Km (min)" value={avgDeliveryTimePerKm} />
                  <DetailField label="Status" value={status} />
                  <DetailField label="Notes" value={notes} wide />
                </div>
              )}
            </div>
            ) : null}

            {activeTab === 'timings' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Timings</h3>
                  <div className="restaurant-detail-card-header-actions">
                    <SectionHeadingActions
                      canEdit={canEditSchedule}
                      editing={isEditingTimings}
                      onEdit={() => setIsEditingTimings(true)}
                      onCancel={handleCancelTimingsEdit}
                      onSave={handleSaveTimings}
                      saving={savingTimings}
                    />
                  </div>
                </div>
                <MealDeliveryTimesEditor
                  value={mealDeliveryTimes}
                  onChange={setMealDeliveryTimes}
                  disabled={!isEditingTimings}
                />
              </div>
            ) : null}

            {activeTab === 'deliveryOptions' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Delivery Options</h3>
                  <div className="restaurant-detail-card-header-actions">
                    <SectionHeadingActions
                      canEdit={canEditSchedule}
                      editing={isEditingSchedule}
                      onEdit={() => setIsEditingSchedule(true)}
                      onCancel={handleCancelScheduleEdit}
                      onSave={handleSaveSchedule}
                      saving={savingSchedule}
                    />
                  </div>
                </div>
                <DeliverySlotsEditor
                  deliveryMode={deliveryMode}
                  deliverySlots={deliverySlots}
                  onModeChange={setDeliveryMode}
                  onSlotsChange={setDeliverySlots}
                  disabled={!isEditingSchedule}
                  showSlotDuration
                  slotDurationMinutes={slotDurationMinutes}
                  onSlotDurationChange={setSlotDurationMinutes}
                  showPartyPrep
                  partyPrepBaseMinutes={partyPrepBaseMinutes}
                  partyPrepMinutesPerPack={partyPrepMinutesPerPack}
                  onPartyPrepChange={({ partyPrepBaseMinutes: base, partyPrepMinutesPerPack: per }) => {
                    setPartyPrepBaseMinutes(base);
                    setPartyPrepMinutesPerPack(per);
                  }}
                  title="Junior Eats & Work Eats delivery"
                  hint="Same schedule as grocery: Regular = ASAP. Fixed = start times with a shared window duration (batched stop-by-stop). Both = either. Meal cutoffs stay under Timings. Party Orders use prep base/per-pack for ETA."
                />
              </div>
            ) : null}

            {activeTab === 'media' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Media</h3>
                </div>
                <KitchenMediaEditor
                  entityType="restaurant"
                  entityId={restaurantId}
                  canEdit={canEditSchedule}
                />
              </div>
            ) : null}

            {activeTab === 'mapOffices' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <WorkEatsMapOfficesPanel
                  restaurantId={restaurantId}
                  userId={user?.userId}
                  disabled={!canManageMaps}
                  onMappedOfficesChange={setMappedWorkEatsOffices}
                />
                <WorkEatsOrgMealDeliveryTimesEditor
                  restaurantId={restaurantId}
                  userId={user?.userId}
                  disabled={!canManageMaps}
                  mappedOffices={mappedWorkEatsOffices}
                />
              </div>
            ) : null}

            {activeTab === 'mapFarmhouses' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <CateringMapFarmhousesPanel
                  restaurantId={restaurantId}
                  userId={user?.userId}
                  disabled={!canManageMaps}
                />
              </div>
            ) : null}

            {activeTab === 'mapSchools' ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <JrEatsMapSchoolsPanel
                  restaurantId={restaurantId}
                  userId={user?.userId}
                  disabled={!canManageMaps}
                />
              </div>
            ) : null}

            {activeTab === 'owner' && embeddedInAdmin ? (
              <OwnerDetailsSection
                title="Owner"
                owner={ownerDetails}
                loading={ownerLoading}
                canEdit={canEdit}
                extraViewFields={[
                  {
                    label: 'FSSAI Number',
                    value: formatFssaiDisplayOrFallback(restaurant?.fssaiNumber),
                  },
                ]}
                onSave={handleSaveOwner}
                emptyHeader={
                  showAddOwner ? (
                    <button
                      type="button"
                      className="eats-admin-header-btn"
                      onClick={() => setShowAddOwner(false)}
                    >
                      Cancel
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="eats-admin-header-btn"
                      onClick={() => setShowAddOwner(true)}
                    >
                      Add User
                    </button>
                  )
                }
                emptyBody={
                  showAddOwner ? (
                    <RootAdminAssignPanel
                      restaurantId={restaurantId}
                      restaurantName={restaurantName || extractRestaurantName(restaurant)}
                      createdById={user?.userId}
                      onSuccess={() => {
                        setShowAddOwner(false);
                        setOwnerReloadToken((token) => token + 1);
                        loadRestaurant();
                      }}
                    />
                  ) : (
                    <p className="restaurant-detail-message">
                      No owner assigned yet. Use Add User to search an existing owner or create a new one.
                    </p>
                  )
                }
              />
            ) : null}

            {activeTab === 'address' ? (
            <div className="restaurant-detail-card restaurant-detail-address-card" role="tabpanel">
              <AddressTable
                addresses={addresses}
                onUpdate={handleAddressUpdate}
                readOnly={!canEdit}
                sectionTitle="Address"
              />
            </div>
            ) : null}

            {activeTab === 'taxes' ? (
              <div role="tabpanel">
                <RestaurantTaxesDisplay restaurantId={restaurantId} />
              </div>
            ) : null}

            {activeTab === 'orderNumbering' ? (
              <div role="tabpanel">
                <OrderNumberingDisplay restaurantId={restaurantId} />
              </div>
            ) : null}

            {activeTab === 'services' ? (
              <div role="tabpanel">
                <RestaurantServicesDisplay
                  restaurantId={restaurantId}
                  reloadToken={servicesReloadToken}
                  onServicesChange={refreshPartyHallFromServices}
                />
              </div>
            ) : null}

            {activeTab === 'partyHall' && partyHallDetails ? (
              <div className="restaurant-detail-card" role="tabpanel">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Party Hall</h3>
                  <div className="restaurant-detail-card-header-actions">
                    {!isEditingPartyHall && partyHallDetails.status ? (
                      <span
                        className={`restaurant-detail-badge restaurant-detail-badge-${String(
                          partyHallDetails.status
                        )
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, '-')}`}
                      >
                        {partyHallDetails.status}
                      </span>
                    ) : null}
                    <SectionHeadingActions
                      canEdit={canEdit}
                      editing={isEditingPartyHall}
                      onEdit={startEditPartyHall}
                      onCancel={cancelEditPartyHall}
                      onSave={handleSavePartyHall}
                      saving={partyHallSaving}
                    />
                  </div>
                </div>
                {isEditingPartyHall ? (
                  <div className="restaurant-services-form">
                    <div className="restaurant-services-form-grid">
                      <label className="restaurant-services-picker-field">
                        <span className="restaurant-services-picker-field-label">Min Guests</span>
                        <input
                          type="number"
                          min="1"
                          value={partyHallEditForm.minGuestCount}
                          onChange={(e) =>
                            setPartyHallEditForm((current) => ({
                              ...current,
                              minGuestCount: e.target.value,
                            }))
                          }
                          disabled={partyHallSaving}
                        />
                      </label>
                      <label className="restaurant-services-picker-field">
                        <span className="restaurant-services-picker-field-label">Max Guests</span>
                        <input
                          type="number"
                          min="1"
                          value={partyHallEditForm.maxGuestCount}
                          onChange={(e) =>
                            setPartyHallEditForm((current) => ({
                              ...current,
                              maxGuestCount: e.target.value,
                            }))
                          }
                          disabled={partyHallSaving}
                          placeholder="Optional"
                        />
                      </label>
                      <PartyHallServiceFields
                        value={partyHallEditForm}
                        onChange={setPartyHallEditForm}
                        disabled={partyHallSaving}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="restaurant-detail-grid">
                    <DetailField label="Hall Name" value={partyHallDetails.hallName} />
                    <DetailField label="Hall Type" value={partyHallDetails.hallTypeLabel} />
                    <DetailField label="Base Price" value={partyHallDetails.basePrice} />
                    <DetailField label="Min Guests" value={partyHallDetails.minGuestCount} />
                    <DetailField label="Max Guests" value={partyHallDetails.maxGuestCount} />
                    <DetailField label="Description" value={partyHallDetails.description} wide />
                    <DetailField label="Amenities" value={partyHallDetails.amenities} wide />
                    <DetailField label="Event Types" value={partyHallDetails.eventTypes} wide />
                  </div>
                )}
                <PartyHallImages
                  partyHallId={partyHallDetails.partyHallId}
                  restaurantId={restaurantId}
                  status={partyHallDetails.status}
                  canEdit={canEdit}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );

  if (embeddedInAdmin) {
    return page;
  }

  return (
    <RestaurantDashboardLayout
      activeTab="business-profile"
      enabledServiceTypes={enabledServiceTypes}
      servicesLoading={servicesLoading}
    >
      {page}
    </RestaurantDashboardLayout>
  );
};

export default RestaurantDetail;
